/**
 * Mesure d'audience **facultative** (DECISIONS §11).
 *
 * Tant que le joueur n'a pas accepté la mesure, ce module ne fait rien : aucune écriture de cookie,
 * aucune clé de stockage, aucun envoi réseau. C'est la contrepartie exécutable de la phrase
 * « aucun cookie non essentiel avant consentement ».
 *
 * Ce que la mesure fait, et rien de plus : un identifiant anonyme tiré au hasard (jamais dérivé
 * d'un compte, d'une adresse ou d'un jeton) et un compteur de visites conservés sur l'appareil.
 * Rien n'est transmis à un tiers : le module n'ouvre aucune connexion.
 */
import { ecrireCookie } from '../i18n/locale-cookie';

/** Cookie de mesure d'audience (facultatif, créé seulement après acceptation). */
export const COOKIE_MESURE = 'elemora_ma';

/** Clé de stockage local du compteur (facultative, créée seulement après acceptation). */
export const CLE_MESURE = 'elemora.mesure-audience';

/** Treize mois, durée maximale annoncée dans la politique de cookies. */
const DUREE_COOKIE_JOURS = 396;

/**
 * Vrai si la mesure a déjà été appliquée pendant ce chargement de page.
 *
 * Le mode strict de React exécute les effets deux fois en développement : sans ce garde-fou, chaque
 * montage compterait deux visites. Ce drapeau ne survit pas à un rechargement, ce qui est voulu.
 */
let appliqueeCetteSession = false;

/** Identifiant anonyme : UUID du navigateur, ou tirage aléatoire s'il n'est pas disponible. */
function identifiantAnonyme(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `anon-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/** Lit l'identifiant posé, sans en créer un. */
export function lireIdentifiantMesure(): string | null {
  try {
    const brut = window.localStorage.getItem(CLE_MESURE);
    if (brut === null) return null;
    const analyse: unknown = JSON.parse(brut);
    if (analyse === null || typeof analyse !== 'object') return null;
    const valeur = (analyse as { identifiant?: unknown }).identifiant;
    return typeof valeur === 'string' && valeur !== '' ? valeur : null;
  } catch {
    return null;
  }
}

/**
 * Active la mesure : identifiant anonyme + compteur de visites.
 *
 * Idempotent : un second appel réutilise l'identifiant existant et incrémente le compteur d'une
 * seule visite par chargement complet de l'application.
 */
export function activerMesureAudience(): void {
  if (appliqueeCetteSession) return;
  appliqueeCetteSession = true;
  const existant = lireIdentifiantMesure();
  const identifiant = existant ?? identifiantAnonyme();
  try {
    const brut = window.localStorage.getItem(CLE_MESURE);
    const visites =
      brut === null ? 0 : Number((JSON.parse(brut) as { visites?: unknown }).visites ?? 0);
    window.localStorage.setItem(
      CLE_MESURE,
      JSON.stringify({
        identifiant,
        visites: Number.isFinite(visites) ? visites + 1 : 1,
        debut: new Date().toISOString(),
      }),
    );
  } catch {
    // Stockage refusé : la mesure est simplement indisponible, le jeu n'en dépend pas.
  }
  ecrireCookie(COOKIE_MESURE, identifiant, DUREE_COOKIE_JOURS);
}

/** Désactive la mesure : supprime le cookie et la clé locale, s'ils existent. */
export function desactiverMesureAudience(): void {
  appliqueeCetteSession = false;
  if (typeof document !== 'undefined') {
    document.cookie = `${COOKIE_MESURE}=; Path=/; Max-Age=0; SameSite=Lax`;
  }
  try {
    window.localStorage.removeItem(CLE_MESURE);
  } catch {
    // Rien à supprimer.
  }
}

/** Applique un choix : la mesure ne tourne que si elle a été explicitement acceptée. */
export function appliquerChoixMesure(mesureAudience: boolean): void {
  if (mesureAudience) activerMesureAudience();
  else desactiverMesureAudience();
}

/**
 * Remet la mesure dans son état initial (tests et diagnostic).
 *
 * Sans cela, le drapeau de session survivrait d’un test à l’autre et une activation ultérieure
 * serait ignorée : le test de consentement pourrait alors passer pour une mauvaise raison.
 */
export function reinitialiserMesureAudience(): void {
  desactiverMesureAudience();
}
